import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  Button,
  Checkbox,
  Input,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  CreditCardPerson20Regular,
  Wallet20Regular,
  BuildingBank20Regular,
  ArrowRight20Regular,
  ChevronRight16Regular,
  Info16Regular,
  Info20Regular,
  Checkmark24Filled,
} from '@fluentui/react-icons';

declare global {
  interface Window {
    Accept: any;
  }
}

export type PaymentMethod = 'credit_card' | 'cash_other' | 'ach';
type ProcessingMode = 'charge' | 'authorize';
type CashSubMethod = 'cash' | 'check' | 'ach' | 'bank_transfer' | 'card_payment' | 'other';
type AchAccountType = 'checking' | 'savings';
type DialogStep = 'methods' | 'credit_card' | 'cash_other' | 'ach';

// Auth.Net Accept.js credentials (public login id + client key) are resolved
// from the backend at runtime via GET /payments/acceptjs-config — the ONE
// shared source (backend/services/payments.resolve_acceptjs_config), the same
// values the hosted pay-link page renders. Previously baked in via
// VITE_AUTHNET_* build-time env vars, which drifted from the backend and went
// stale on any env change without a frontend rebuild (→ E00007 at tokenization).
type AcceptJsConfig = {
  loginId: string;
  clientKey: string;
  environment: string;
  jsUrl: string;
};

// Bump when NACHA disclosure text changes — keep in lockstep with the
// website's PaymentForm so both surfaces report the same disclosure_version
// in payment_authorizations.
const ACH_DISCLOSURE_VERSION = '1.0';
const TWM_SUPPORT_PHONE = '(939) 238-7440';

// ABA mod-10 checksum for 9-digit US routing numbers. Catches single-digit
// typos and adjacent-digit transpositions before the request leaves the
// browser. Mirrors the website's validateABA.
function validateABA(routing: string): boolean {
  if (!/^\d{9}$/.test(routing)) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = routing.split('').reduce((acc, d, i) => acc + parseInt(d, 10) * weights[i], 0);
  return sum % 10 === 0;
}

async function sha256Hex(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(text));
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// NACHA-compliant one-time authorization. Same shape as the website's
// buildDisclosureText — staff-initiated debits must satisfy the same
// "clear and conspicuous" rule as customer-initiated ones.
function buildAchDisclosureText(amount: number): string {
  const today = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  return (
    `By providing the bank account information above and checking the box below, the account holder authorizes Tradewinds Mechanical LLC to initiate a one-time electronic debit from the identified checking or savings account, in the amount of $${amount.toFixed(2)}, on or after ${today}. The account holder certifies they are an authorized signer on this account and that the funds are available. This authorization cannot be revoked once the transaction is submitted. For questions or refund requests, contact Tradewinds Mechanical LLC at info@tradewindsmechanical.com.`
  );
}

// Technology fee — MIRRORS backend/services/fees.py. Keep in sync.
const TECHNOLOGY_FEE_RATE = 0.03;
// Whether the fee applies to ACH. false = ACH fee-free (current client
// direction, 2026-06-12); flip to true to gross up ACH like card. Mirrors
// ACH_TECHNOLOGY_FEE_ENABLED in backend/services/fees.py.
const ACH_TECHNOLOGY_FEE_ENABLED = false;

/** Technology fee added on top of the base for a capture, so the operator sees
 *  the real total. Mirrors the server gross-up: card = base * rate/(1-rate).
 *  ACH is fee-free while ACH_TECHNOLOGY_FEE_ENABLED is false; holds (authorize)
 *  carry no fee. */
export function techFeeCents(baseCents: number, method: 'card' | 'ach', mode: ProcessingMode): number {
  if (baseCents <= 0 || mode !== 'charge') return 0;
  if (method === 'ach' && !ACH_TECHNOLOGY_FEE_ENABLED) return 0;
  return Math.round(baseCents * (TECHNOLOGY_FEE_RATE / (1 - TECHNOLOGY_FEE_RATE)));
}

export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
}

/** Optional metadata the parent can return from onCharge so the success
 *  screen can swap the default "Paid" confirmation for an info-style banner.
 *  Today only `deduped` is used (E00039 — card already saved), but the shape
 *  is left open so future flows (e.g. ACH duplicate detection) can piggyback.
 */
export interface ChargeResult {
  /** True when the backend reused an existing CIM payment profile because
   *  the card the operator just entered was already on file for this
   *  customer. Charge still succeeded — this just changes the confirmation
   *  copy. */
  deduped?: boolean;
  /** CIM payment_profile_id that was used / created / reused. */
  payment_profile_id?: string | null;
}

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string | number | null;
  orderAmountInCents: number;
  orderPaidInCents: number;
  securityDepositInCents?: number;
  securityDepositPaidInCents?: number;
  /** The order's persisted technology-fee waiver (orders.waive_tech_fee).
   *  Pre-checks the per-charge waive toggle so a waived order never quotes or
   *  charges a fee. The backend enforces this authoritatively too. (B4-3.) */
  orderWaivesTechFee?: boolean;
  /** Cards on file for this customer. When present, a chip renders inside
   *  the Card number row so staff can charge a saved card without
   *  re-entering details. */
  savedCards?: SavedCard[];
  /** Customer display name — interpolated into the "card already saved for
   *  {name}" banner shown when the backend reports E00039 / deduped=true. */
  customerName?: string | null;
  onCharge?: (payload: {
    method: PaymentMethod;
    mode?: ProcessingMode;
    orderCents: number;
    depositCents: number;
    card?: { nameOnCard: string; cardNumber: string; expiry: string; cvc: string };
    savedCardId?: string;
    /** Credit-card-only. Accept.js opaqueData from tokenizing a freshly-typed
     *  card. Mutually exclusive with `saved_payment_profile_id`. */
    payment_nonce?: { dataDescriptor: string; dataValue: string };
    /** Credit-card-only. Auth.Net CIM customerPaymentProfileId — set when
     *  the operator clicked a saved-card chip. */
    saved_payment_profile_id?: string;
    /** Credit-card-only. When true and a new card was entered, the backend
     *  saves it to CIM after the charge succeeds. */
    save_card?: boolean;
    /** Card/ACH. Operator chose to waive the technology fee for this charge —
     *  the backend captures the base amount only. Scoped to this transaction;
     *  never persisted. */
    skipTechFee?: boolean;
    cashSubMethod?: CashSubMethod;
    cashDate?: string;
    /** ACH-only. Accept.js opaqueData (bankData tokenization) + NACHA audit
     *  payload. The backend persists the audit row into payment_authorizations
     *  only after a successful charge. */
    achNonce?: { dataDescriptor: string; dataValue: string };
    achAuthorization?: {
      disclosure_version: string;
      disclosure_version_hash: string;
      disclosure_text: string;
      account_holder_name: string;
      account_last4: string;
      account_type: AchAccountType;
      authorized_at: string;
    };
  }) => Promise<ChargeResult | void> | ChargeResult | void;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: '900px',
    width: '100%',
    padding: 0,
    // Cap to viewport so the footer buttons never fall below the fold on
    // short laptop screens. 32px reserves a bit of breathing room.
    maxHeight: 'calc(100vh - 32px)',
    ...shorthands.overflow('hidden'),
    // Below 768px the 900px desktop dialog (260px rail + main) overflows
    // the viewport. Switch to a full-screen sheet so the body can stack
    // and scroll.
    '@media (max-width: 768px)': {
      maxWidth: '100vw',
      width: '100vw',
      maxHeight: '100vh',
      height: '100vh',
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    // Tall enough for the credit-card step with saved cards visible AND the
    // new-card form expanded — prevents the dialog from leaping in height
    // when the operator toggles between a saved card and "Use a new card".
    // Capped at viewport so short laptop screens don't push the footer
    // below the fold; `min()` resolves to whichever is smaller.
    minHeight: 'min(620px, calc(100vh - 32px))',
    // Cap body height to surface height (which is viewport-capped) so the
    // child `main` actually has a bounded container to scroll inside of.
    maxHeight: 'calc(100vh - 32px)',
    // Anchor for the absolutely-positioned close button so it pins to the
    // top-right of the dialog regardless of main's scroll position.
    position: 'relative',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      minHeight: '100vh',
      maxHeight: '100vh',
      ...shorthands.overflow('auto'),
    },
  },
  rail: {
    width: '260px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('28px', '24px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '@media (max-width: 768px)': {
      width: '100%',
      boxSizing: 'border-box',
      ...shorthands.padding('16px', '20px'),
      gap: '12px',
    },
  },
  railLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  railAmount: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.2,
  },
  railDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  railRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  railSubRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    // No padding / overflow here — the inner scroll area owns those so the
    // footer can sit outside the scrollable region.
    minHeight: 0,
  },
  mainScroll: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    overflowY: 'auto',
    ...shorthands.padding('24px', '28px'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('20px', '16px'),
    },
  },
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    minWidth: 'auto',
    // Above the main panel's scroll region + above the sticky footer so the
    // close affordance is always reachable.
    zIndex: 2,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginTop: '4px',
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    marginTop: '2px',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  breadcrumbSeparator: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  breadcrumbCrumb: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  amountRow: {
    display: 'flex',
    gap: '16px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  amountField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  amountLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  amountMax: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    marginLeft: '8px',
    whiteSpace: 'nowrap',
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginTop: '4px',
  },
  methodsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  methodCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    ...shorthands.padding('14px', '16px'),
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    transitionProperty: 'background-color, border-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
  },
  methodIcon: {
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  methodText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  methodTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  methodDesc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  methodArrow: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  modeRow: {
    display: 'flex',
    gap: '12px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  modeCard: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('14px', '16px'),
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    transitionProperty: 'background-color, border-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  modeCardSelected: {
    ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
    ...shorthands.padding('13px', '15px'),
    backgroundColor: tokens.colorBrandBackground2,
  },
  modeRadio: {
    width: '18px',
    height: '18px',
    ...shorthands.borderRadius('50%'),
    ...shorthands.border('2px', 'solid', tokens.colorNeutralStroke1),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  modeRadioSelected: {
    ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
  },
  modeRadioDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandBackground,
  },
  modeLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  modeHelp: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    marginTop: '-8px',
  },
  cardPanel: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  cardPanelLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  cardInput: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('10px', '12px'),
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  cardNumberRow: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('4px', '12px'),
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardNumberInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  cardExpiryInput: {
    width: '28px',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  cardExpirySlash: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    userSelect: 'none',
  },
  cardCvcInput: {
    width: '44px',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  savedCardChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralBackground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    cursor: 'pointer',
    ...shorthands.border('0'),
  },
  savedCardChipSelected: {
    backgroundColor: tokens.colorBrandBackground,
  },
  savedCardCheck: {
    width: '14px',
    height: '14px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
  },
  spacer: {
    flex: 1,
  },
  dateInputWrap: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('8px', '12px'),
  },
  dateInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  dateClearBtn: {
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    ...shorthands.padding('0'),
    display: 'flex',
    alignItems: 'center',
  },
  methodGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  successFullBody: {
    flex: 1,
    width: '100%',
    minHeight: '460px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('40px'),
    boxSizing: 'border-box',
  },
  successPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  successCheckRing: {
    width: '56px',
    height: '56px',
    ...shorthands.borderRadius('50%'),
    ...shorthands.border('3px', 'solid', tokens.colorPaletteGreenForeground1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorPaletteGreenForeground1,
  },
  successAmount: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  // Info-style banner shown on the success screen when the backend reports
  // the card was deduped (E00039). Neutral info palette — this is "we did
  // the right thing", not a warning or an error.
  dedupeBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    ...shorthands.padding('12px', '14px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    maxWidth: '420px',
    textAlign: 'left',
  },
  dedupeBannerIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    marginTop: '2px',
  },
  feeNote: {
    marginRight: 'auto',
    alignSelf: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('14px', '28px'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('14px', '16px'),
    },
  },
  achInputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  achLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  achInput: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('10px', '12px'),
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  achInputError: {
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
  },
  achError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  achTwoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  achAuthPanel: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('14px', '16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  achDisclosure: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
  },
  achSettlementNote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

const formatMoney = (cents: number) => {
  const v = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${v}`;
};

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  orderNumber,
  orderAmountInCents,
  orderPaidInCents,
  securityDepositInCents = 0,
  securityDepositPaidInCents = 0,
  orderWaivesTechFee = false,
  savedCards = [],
  customerName,
  onCharge,
}) => {
  const styles = useStyles();

  const orderDueCents = Math.max(0, orderAmountInCents - orderPaidInCents);
  const depositDueCents = Math.max(0, securityDepositInCents - securityDepositPaidInCents);
  const totalDueCents = orderDueCents + depositDueCents;

  const [step, setStep] = useState<DialogStep>('methods');
  const [orderAmount, setOrderAmount] = useState<string>('');
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [processingMode, setProcessingMode] = useState<ProcessingMode>('charge');
  const [nameOnCard, setNameOnCard] = useState<string>('');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [cardExpiryMM, setCardExpiryMM] = useState<string>('');
  const [cardExpiryYY, setCardExpiryYY] = useState<string>('');
  const [cardCvc, setCardCvc] = useState<string>('');
  const [selectedSavedCardId, setSelectedSavedCardId] = useState<string | null>(null);
  const [saveCardOnFile, setSaveCardOnFile] = useState<boolean>(false);
  // Operator opt-in to waive the technology fee on this charge (e.g. a one-off
  // verification charge that shouldn't carry the fee). Per-transaction — never
  // persisted. Defaults from the order's persisted waiver on open (B4-3); the
  // operator can still toggle it for this charge.
  const [waiveFee, setWaiveFee] = useState<boolean>(false);
  const yyInputRef = useRef<HTMLInputElement | null>(null);
  const [cashSubMethod, setCashSubMethod] = useState<CashSubMethod>('cash');
  const [cashDate, setCashDate] = useState<string>('');
  // ACH state
  const [achAccountHolderName, setAchAccountHolderName] = useState<string>('');
  const [achAccountType, setAchAccountType] = useState<AchAccountType>('checking');
  const [achRoutingNumber, setAchRoutingNumber] = useState<string>('');
  const [achAccountNumber, setAchAccountNumber] = useState<string>('');
  const [achConfirmAccountNumber, setAchConfirmAccountNumber] = useState<string>('');
  const [achAuthorized, setAchAuthorized] = useState<boolean>(false);
  const [achErrors, setAchErrors] = useState<{
    accountHolderName?: string;
    routingNumber?: string;
    accountNumber?: string;
    confirmAccountNumber?: string;
    achAuthorized?: string;
  }>({});
  const [acceptJsReady, setAcceptJsReady] = useState<boolean>(false);
  // Accept.js public credentials, fetched from the backend (one shared source)
  // the first time the dialog opens. staleTime Infinity: config only changes on
  // a key rotation, which redeploys the backend anyway; a page reload refetches.
  const { data: anConfig } = useQuery<AcceptJsConfig>({
    queryKey: ['acceptjs-config'],
    queryFn: async () => {
      const res = await apiFetch('/payments/acceptjs-config');
      if (!res.ok) throw new Error(`acceptjs-config ${res.status}`);
      return res.json();
    },
    enabled: open,
    staleTime: Infinity,
  });
  const acceptJsUrl = anConfig?.jsUrl;
  const anLoginId = anConfig?.loginId || '';
  const anClientKey = anConfig?.clientKey || '';
  // null while collecting input; set to the paid amount once the user clicks
  // the action button so the success overlay can render. Cleared on close
  // along with the rest of the form.
  const [successAmountCents, setSuccessAmountCents] = useState<number | null>(null);
  // True when the backend reports the just-entered card was already on file
  // (Auth.Net E00039). Drives the info-style "card already saved" banner on
  // the success screen. Reset alongside successAmountCents on dialog open.
  const [successDeduped, setSuccessDeduped] = useState<boolean>(false);
  // Tracks whether the just-submitted payment was an authorize-only (hold)
  // vs a direct charge, so the success screen says "Authorized $X" instead
  // of the misleading "Paid $X" for held auths. Reset alongside the rest of
  // the success state on dialog open.
  const [successMode, setSuccessMode] = useState<ProcessingMode>('charge');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset to the method picker + refill amounts each time the dialog opens.
  useEffect(() => {
    if (open) {
      setStep('methods');
      setOrderAmount((orderDueCents / 100).toFixed(2));
      setDepositAmount((depositDueCents / 100).toFixed(2));
      setProcessingMode('charge');
      setNameOnCard('');
      setCardNumber('');
      setCardExpiryMM('');
      setCardExpiryYY('');
      setCardCvc('');
      setSelectedSavedCardId(null);
      setSaveCardOnFile(false);
      setWaiveFee(orderWaivesTechFee);
      setCashSubMethod('cash');
      // ISO yyyy-mm-dd, the value shape <input type="date"> expects.
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setCashDate(iso);
      setAchAccountHolderName('');
      setAchAccountType('checking');
      setAchRoutingNumber('');
      setAchAccountNumber('');
      setAchConfirmAccountNumber('');
      setAchAuthorized(false);
      setAchErrors({});
      setSuccessAmountCents(null);
      setSuccessDeduped(false);
      setSuccessMode('charge');
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open, orderDueCents, depositDueCents, orderWaivesTechFee]);

  // Lazy-load Accept.js the first time the dialog opens. The script is
  // shared by the card and ACH paths — both call window.Accept.dispatchData
  // for tokenization — but only ACH currently relies on it (card path is
  // still unwired). Loaded on open rather than on mount so we don't pull
  // it on every order page view.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;
    // Wait for the backend-resolved Accept.js URL before loading the SDK, so we
    // always load the environment-correct script (sandbox vs prod).
    if (!acceptJsUrl) return;
    if (window.Accept) {
      setAcceptJsReady(true);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${acceptJsUrl}"]`);
    if (existing) {
      existing.addEventListener('load', () => setAcceptJsReady(true), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = acceptJsUrl;
    script.async = true;
    script.onload = () => setAcceptJsReady(true);
    script.onerror = () => setSubmitError('Failed to load payment SDK. Please refresh and try again.');
    document.head.appendChild(script);
  }, [open, acceptJsUrl]);

  const parseDollars = (s: string) => Math.round((parseFloat(s) || 0) * 100);

  const clampToMax = (value: string, maxCents: number): string => {
    const cents = parseDollars(value);
    if (cents <= maxCents) return value;
    return (maxCents / 100).toFixed(2);
  };

  const chargeTotalCents = parseDollars(orderAmount) + parseDollars(depositAmount);
  // Grossed-up total shown to the operator on the card path. ACH carries no
  // fee, so the ACH debit is just the base (chargeTotalCents).
  const cardFeeCents = waiveFee ? 0 : techFeeCents(chargeTotalCents, 'card', processingMode);
  const cardChargeTotalCents = chargeTotalCents + cardFeeCents;

  const runSubmit = async (payload: Parameters<NonNullable<typeof onCharge>>[0]) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // onCharge may resolve to a ChargeResult with `deduped: true` when the
      // backend detected E00039 (card already saved). The charge itself
      // still went through — we just swap the success copy.
      const result = await onCharge?.(payload);
      const deduped = !!(result && (result as ChargeResult).deduped);
      // Happy path: dismiss immediately so the operator returns to the
      // order page and sees the new payment row appear. The parent's
      // success toast is the confirmation. Edge cases worth lingering on
      // (currently: card-already-saved dedupe banner) keep the dialog
      // open so the operator can read the explanation.
      if (deduped) {
        setSuccessDeduped(true);
        setSuccessMode(payload.mode || 'charge');
        // Dedupe only happens on the card path; show the grossed-up total.
        setSuccessAmountCents(payload.mode === 'charge' ? cardChargeTotalCents : chargeTotalCents);
      } else {
        onClose();
      }
    } catch (e: any) {
      setSubmitError(e?.message || 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const tokenizeCard = (): Promise<{ dataDescriptor: string; dataValue: string }> => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.Accept) {
        reject(new Error('Payment SDK not loaded'));
        return;
      }
      if (!anLoginId || !anClientKey) {
        reject(new Error('Payment SDK is misconfigured (missing Accept.js credentials from the server)'));
        return;
      }
      const secureData = {
        authData: { clientKey: anClientKey, apiLoginID: anLoginId },
        cardData: {
          cardNumber,
          month: cardExpiryMM,
          // Accept.js wants a 4-digit year; the dialog collects 2-digit and
          // we expand to 20YY. Revisit in 2100.
          year: `20${cardExpiryYY}`,
          cardCode: cardCvc,
        },
      };
      window.Accept.dispatchData(secureData, (response: any) => {
        if (response?.opaqueData) {
          resolve(response.opaqueData);
        } else {
          const msgs = response?.messages?.message || [];
          reject(new Error(msgs.map((m: any) => m.text).join('; ') || 'Card tokenization failed'));
        }
      });
    });
  };

  const validateNewCardFields = (): string | null => {
    if (!cardNumber || cardNumber.length < 13) return 'Enter a valid card number';
    if (cardExpiryMM.length !== 2 || cardExpiryYY.length !== 2) return 'Enter card expiration (MM/YY)';
    if (cardCvc.length < 3) return 'Enter the card CVC';
    return null;
  };

  const handleCharge = async () => {
    if (selectedSavedCardId) {
      runSubmit({
        method: 'credit_card',
        mode: processingMode,
        orderCents: parseDollars(orderAmount),
        depositCents: parseDollars(depositAmount),
        saved_payment_profile_id: selectedSavedCardId,
        skipTechFee: waiveFee,
      });
      return;
    }

    const validationError = validateNewCardFields();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    if (!acceptJsReady) {
      setSubmitError('Payment SDK is still loading. Try again in a moment.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    let nonce: { dataDescriptor: string; dataValue: string };
    try {
      nonce = await tokenizeCard();
    } catch (e: any) {
      setSubmitError(e?.message || 'Card tokenization failed');
      setSubmitting(false);
      return;
    }
    // runSubmit owns submitting/success/error state — release our local flag
    // so it can re-acquire without flickering the button.
    setSubmitting(false);

    runSubmit({
      method: 'credit_card',
      mode: processingMode,
      orderCents: parseDollars(orderAmount),
      depositCents: parseDollars(depositAmount),
      payment_nonce: nonce,
      save_card: saveCardOnFile,
      skipTechFee: waiveFee,
    });
  };

  const handleCashPay = () => {
    runSubmit({
      method: 'cash_other',
      orderCents: parseDollars(orderAmount),
      depositCents: parseDollars(depositAmount),
      cashSubMethod,
      cashDate,
    });
  };

  // --- ACH handlers ---
  const handleAchRoutingChange = (value: string) => {
    setAchRoutingNumber(value.replace(/\D/g, '').slice(0, 9));
    if (achErrors.routingNumber) setAchErrors((prev) => ({ ...prev, routingNumber: undefined }));
  };
  const handleAchAccountNumberChange = (value: string) => {
    setAchAccountNumber(value.replace(/\D/g, '').slice(0, 17));
    if (achErrors.accountNumber || achErrors.confirmAccountNumber) {
      setAchErrors((prev) => ({ ...prev, accountNumber: undefined, confirmAccountNumber: undefined }));
    }
  };
  const handleAchConfirmAccountNumberChange = (value: string) => {
    setAchConfirmAccountNumber(value.replace(/\D/g, '').slice(0, 17));
    if (achErrors.confirmAccountNumber) setAchErrors((prev) => ({ ...prev, confirmAccountNumber: undefined }));
  };
  const handleAchHolderNameChange = (value: string) => {
    setAchAccountHolderName(value);
    if (achErrors.accountHolderName) setAchErrors((prev) => ({ ...prev, accountHolderName: undefined }));
  };
  const handleAchAuthorizedChange = (checked: boolean) => {
    setAchAuthorized(checked);
    if (checked && achErrors.achAuthorized) setAchErrors((prev) => ({ ...prev, achAuthorized: undefined }));
  };

  const validateAch = (): boolean => {
    const errors: typeof achErrors = {};
    if (!achAccountHolderName.trim()) errors.accountHolderName = 'Enter the name on the account';
    if (!/^\d{9}$/.test(achRoutingNumber)) {
      errors.routingNumber = 'Routing number must be 9 digits';
    } else if (!validateABA(achRoutingNumber)) {
      errors.routingNumber = 'Routing number checksum invalid — double-check the digits';
    }
    if (achAccountNumber.length < 4 || achAccountNumber.length > 17) {
      errors.accountNumber = 'Account number must be 4–17 digits';
    }
    if (achConfirmAccountNumber !== achAccountNumber) {
      errors.confirmAccountNumber = 'Account numbers do not match';
    }
    if (!achAuthorized) {
      errors.achAuthorized = 'Authorize the electronic debit to continue';
    }
    setAchErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tokenizeBankAccount = (): Promise<{ dataDescriptor: string; dataValue: string }> => {
    if (!anLoginId || !anClientKey) {
      return Promise.reject(
        new Error('Payment SDK is misconfigured (missing Accept.js credentials from the server)'),
      );
    }
    const secureData = {
      authData: {
        clientKey: anClientKey,
        apiLoginID: anLoginId,
      },
      bankData: {
        accountType: achAccountType,
        routingNumber: achRoutingNumber,
        accountNumber: achAccountNumber,
        // Auth.Net caps nameOnAccount at 22 chars — silent truncation beats
        // tokenization rejection for long legal names.
        nameOnAccount: achAccountHolderName.trim().slice(0, 22),
      },
    };
    return new Promise((resolve, reject) => {
      window.Accept.dispatchData(secureData, (response: any) => {
        if (response.opaqueData) {
          resolve(response.opaqueData);
        } else {
          const msgs = response.messages?.message || [];
          const errorText = msgs.map((m: any) => m.text).join('; ') || 'Bank account tokenization failed';
          reject(new Error(errorText));
        }
      });
    });
  };

  const handleAchPay = async () => {
    if (!validateAch()) return;
    if (!acceptJsReady || typeof window === 'undefined' || !window.Accept) {
      setSubmitError('Payment SDK is not ready. Please wait a moment and try again.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Build the NACHA audit payload *before* tokenizing so authorized_at
      // reflects when the operator committed (clicked Authorize), not when
      // Accept.js happened to return.
      const disclosureText = buildAchDisclosureText(chargeTotalCents / 100);
      const achAuth = {
        disclosure_version: ACH_DISCLOSURE_VERSION,
        disclosure_version_hash: await sha256Hex(disclosureText),
        disclosure_text: disclosureText,
        account_holder_name: achAccountHolderName.trim(),
        account_last4: achAccountNumber.slice(-4),
        account_type: achAccountType,
        authorized_at: new Date().toISOString(),
      };
      const opaqueData = await tokenizeBankAccount();
      await onCharge?.({
        method: 'ach',
        orderCents: parseDollars(orderAmount),
        depositCents: parseDollars(depositAmount),
        achNonce: {
          dataDescriptor: opaqueData.dataDescriptor,
          dataValue: opaqueData.dataValue,
        },
        achAuthorization: achAuth,
        skipTechFee: waiveFee,
      });
      // ACH has no dedup path — always close on success; toast confirms.
      onClose();
    } catch (e: any) {
      setSubmitError(e?.message || 'ACH payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderMethodsStep = () => (
    <>
      <div className={styles.title}>Payment for order #{orderNumber ?? ''}</div>

      <div className={styles.amountRow}>
        <div className={styles.amountField}>
          <label className={styles.amountLabel}>Order amount</label>
          <Input
            size="large"
            value={orderAmount}
            onChange={(_, data) => setOrderAmount(data.value)}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => setOrderAmount(clampToMax(e.target.value, orderDueCents))}
            inputMode="decimal"
            contentBefore="$"
            contentAfter={<span className={styles.amountMax}>/ {formatMoney(orderDueCents)}</span>}
          />
        </div>
      </div>

      <div className={styles.sectionLabel}>Payment methods</div>
      <div className={styles.methodsList}>
        <button type="button" className={styles.methodCard} onClick={() => setStep('credit_card')}>
          <CreditCardPerson20Regular className={styles.methodIcon} />
          <div className={styles.methodText}>
            <span className={styles.methodTitle}>Credit card</span>
            <span className={styles.methodDesc}>Use a saved creditcard or other payment method.</span>
          </div>
          <ArrowRight20Regular className={styles.methodArrow} />
        </button>
        <button type="button" className={styles.methodCard} onClick={() => setStep('ach')}>
          <BuildingBank20Regular className={styles.methodIcon} />
          <div className={styles.methodText}>
            <span className={styles.methodTitle}>Bank account (ACH)</span>
            <span className={styles.methodDesc}>Debit a checking or savings account. Settles in 1–3 business days.</span>
          </div>
          <ArrowRight20Regular className={styles.methodArrow} />
        </button>
        <button type="button" className={styles.methodCard} onClick={() => setStep('cash_other')}>
          <Wallet20Regular className={styles.methodIcon} />
          <div className={styles.methodText}>
            <span className={styles.methodTitle}>Cash / other</span>
            <span className={styles.methodDesc}>Accept any method of payment outside the system.</span>
          </div>
          <ArrowRight20Regular className={styles.methodArrow} />
        </button>
      </div>
    </>
  );

  const renderCreditCardStep = () => (
    <>
      <div className={styles.breadcrumb}>
        <span className={styles.title}>Payment for order #{orderNumber ?? ''}</span>
        <ChevronRight16Regular className={styles.breadcrumbSeparator} />
        <span className={styles.breadcrumbCrumb}>Credit card</span>
      </div>

      <div className={styles.sectionLabel}>Processing method</div>
      <div className={styles.modeRow}>
        <div
          className={`${styles.modeCard} ${processingMode === 'charge' ? styles.modeCardSelected : ''}`}
          onClick={() => setProcessingMode('charge')}
          role="radio"
          aria-checked={processingMode === 'charge'}
        >
          <div className={`${styles.modeRadio} ${processingMode === 'charge' ? styles.modeRadioSelected : ''}`}>
            {processingMode === 'charge' && <div className={styles.modeRadioDot} />}
          </div>
          <span className={styles.modeLabel}>Charge directly</span>
        </div>
        <div
          className={`${styles.modeCard} ${processingMode === 'authorize' ? styles.modeCardSelected : ''}`}
          onClick={() => setProcessingMode('authorize')}
          role="radio"
          aria-checked={processingMode === 'authorize'}
        >
          <div className={`${styles.modeRadio} ${processingMode === 'authorize' ? styles.modeRadioSelected : ''}`}>
            {processingMode === 'authorize' && <div className={styles.modeRadioDot} />}
          </div>
          <span className={styles.modeLabel}>Authorize payment</span>
        </div>
      </div>
      <div className={styles.modeHelp}>
        <Info16Regular />
        <span>
          {processingMode === 'charge'
            ? 'Immediately charge the paid amount.'
            : 'Reserve the amount and charge when the payment is confirmed.'}
        </span>
      </div>

      {savedCards.length > 0 && (
        <>
          <div className={styles.sectionLabel}>Payment source</div>
          <div className={styles.methodsList}>
            {savedCards.map((card) => {
              const selected = selectedSavedCardId === card.id;
              return (
                <div
                  key={card.id}
                  className={`${styles.modeCard} ${selected ? styles.modeCardSelected : ''}`}
                  onClick={() => {
                    setSelectedSavedCardId(card.id);
                    setCardNumber('');
                    setCardExpiryMM('');
                    setCardExpiryYY('');
                    setCardCvc('');
                    setNameOnCard('');
                    setSubmitError(null);
                  }}
                  role="radio"
                  aria-checked={selected}
                >
                  <div className={`${styles.modeRadio} ${selected ? styles.modeRadioSelected : ''}`}>
                    {selected && <div className={styles.modeRadioDot} />}
                  </div>
                  <CreditCardPerson20Regular className={styles.methodIcon} />
                  <span className={styles.modeLabel}>{card.brand} •••• {card.last4}</span>
                </div>
              );
            })}
            <div
              className={`${styles.modeCard} ${selectedSavedCardId === null ? styles.modeCardSelected : ''}`}
              onClick={() => {
                setSelectedSavedCardId(null);
                setSubmitError(null);
              }}
              role="radio"
              aria-checked={selectedSavedCardId === null}
            >
              <div className={`${styles.modeRadio} ${selectedSavedCardId === null ? styles.modeRadioSelected : ''}`}>
                {selectedSavedCardId === null && <div className={styles.modeRadioDot} />}
              </div>
              <CreditCardPerson20Regular className={styles.methodIcon} />
              <span className={styles.modeLabel}>Use a new card</span>
            </div>
          </div>
        </>
      )}

      {!selectedSavedCardId && (
        <div className={styles.cardPanel}>
          <div className={styles.cardPanelLabel}>
            {savedCards.length > 0 ? 'New card details' : 'Add credit card details'}
          </div>
          <input
            className={styles.cardInput}
            placeholder="Name on card"
            value={nameOnCard}
            onChange={(e) => setNameOnCard(e.target.value)}
          />
          <div className={styles.cardNumberRow}>
            <CreditCardPerson20Regular style={{ color: tokens.colorNeutralForeground3 }} />
            <input
              className={styles.cardNumberInput}
              placeholder="Card number"
              value={cardNumber}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 16);
                setCardNumber(digitsOnly);
              }}
              inputMode="numeric"
              maxLength={16}
            />
            <input
              className={styles.cardExpiryInput}
              placeholder="MM"
              value={cardExpiryMM}
              onChange={(e) => {
                // Month validation: 01-12. Auto-prepend "0" when the user
                // types a digit 2-9 first so the field jumps straight to
                // a valid month (e.g. "5" → "05"). Drop the second digit
                // when it produces MM=00 or MM>12.
                let digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                if (digits.length === 1 && parseInt(digits, 10) > 1) {
                  digits = `0${digits}`;
                }
                if (digits.length === 2) {
                  const mm = parseInt(digits, 10);
                  if (mm === 0 || mm > 12) digits = digits.slice(0, 1);
                }
                setCardExpiryMM(digits);
                // Hop to YY once month is complete so staff can keep typing
                // without reaching for the mouse.
                if (digits.length === 2) yyInputRef.current?.focus();
              }}
              inputMode="numeric"
              maxLength={2}
            />
            <span className={styles.cardExpirySlash}>/</span>
            <input
              ref={yyInputRef}
              className={styles.cardExpiryInput}
              placeholder="YY"
              value={cardExpiryYY}
              onChange={(e) => {
                // Year validation: drop the second digit when the resulting
                // MM/YY is in the past relative to the current month.
                let digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                if (digits.length === 2 && cardExpiryMM.length === 2) {
                  const now = new Date();
                  const currentYY = now.getFullYear() % 100;
                  const currentMM = now.getMonth() + 1;
                  const mm = parseInt(cardExpiryMM, 10);
                  const yy = parseInt(digits, 10);
                  if (yy < currentYY || (yy === currentYY && mm < currentMM)) {
                    digits = digits.slice(0, 1);
                  }
                }
                setCardExpiryYY(digits);
              }}
              inputMode="numeric"
              maxLength={2}
            />
            <input
              className={styles.cardCvcInput}
              placeholder="CVC"
              value={cardCvc}
              onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              maxLength={4}
            />
          </div>
          <Checkbox
            checked={saveCardOnFile}
            onChange={(_, data) => setSaveCardOnFile(!!data.checked)}
            label="Save card on file for future payments"
          />
        </div>
      )}

      <Checkbox
        checked={waiveFee}
        onChange={(_, data) => setWaiveFee(!!data.checked)}
        label="Waive technology fee (charge base amount only)"
      />

      {submitError && <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>{submitError}</div>}
    </>
  );

  const renderCreditCardFooter = () => (
    <div className={styles.footer}>
      <Button appearance="secondary" onClick={() => setStep('methods')} disabled={submitting}>Back</Button>
      {cardFeeCents > 0 && (
        <span className={styles.feeNote}>
          Includes {formatMoney(cardFeeCents)} technology fee
        </span>
      )}
      <Button
        appearance="primary"
        onClick={handleCharge}
        disabled={chargeTotalCents <= 0 || submitting || (!selectedSavedCardId && !acceptJsReady)}
      >
        {submitting
          ? 'Processing…'
          : !selectedSavedCardId && !acceptJsReady
            ? 'Loading payment SDK…'
            : processingMode === 'charge'
              ? `Charge ${formatMoney(cardChargeTotalCents)}`
              : `Authorize ${formatMoney(chargeTotalCents)}`}
      </Button>
    </div>
  );

  const renderCashOtherStep = () => {
    // Manual/off-platform payment sub-methods. These record a payment the
    // operator received OUTSIDE the admin (no card/ACH rail runs) — e.g. a
    // customer who paid via ACH on a pay link for a now-canceled order, whose
    // funds we're crediting to the order that survived. ACH + Check are
    // bookkeeping records here, distinct from the live ACH rail on the
    // "Bank account (ACH)" method.
    const subMethods: { value: CashSubMethod; label: string }[] = [
      { value: 'cash', label: 'Cash' },
      { value: 'check', label: 'Check' },
      { value: 'ach', label: 'ACH' },
      { value: 'bank_transfer', label: 'Bank transfer' },
      { value: 'card_payment', label: 'Card payment' },
      { value: 'other', label: 'Other' },
    ];
    return (
      <>
        <div className={styles.breadcrumb}>
          <span className={styles.title}>Payment for order #{orderNumber ?? ''}</span>
          <ChevronRight16Regular className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCrumb}>Cash / other</span>
        </div>

        <div className={styles.amountField}>
          <label className={styles.amountLabel}>Date</label>
          <div className={styles.dateInputWrap}>
            <input
              type="date"
              className={styles.dateInput}
              value={cashDate}
              onChange={(e) => setCashDate(e.target.value)}
            />
            {cashDate && (
              <button
                type="button"
                className={styles.dateClearBtn}
                onClick={() => setCashDate('')}
                aria-label="Clear date"
              >
                <Dismiss24Regular style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        </div>

        <div className={styles.sectionLabel}>Payment methods</div>
        <div className={styles.methodGrid}>
          {subMethods.map((m) => {
            const selected = cashSubMethod === m.value;
            return (
              <div
                key={m.value}
                className={`${styles.modeCard} ${selected ? styles.modeCardSelected : ''}`}
                onClick={() => setCashSubMethod(m.value)}
                role="radio"
                aria-checked={selected}
              >
                <div className={`${styles.modeRadio} ${selected ? styles.modeRadioSelected : ''}`}>
                  {selected && <div className={styles.modeRadioDot} />}
                </div>
                <span className={styles.modeLabel}>{m.label}</span>
              </div>
            );
          })}
        </div>

        {submitError && <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>{submitError}</div>}
      </>
    );
  };

  const renderCashOtherFooter = () => (
    <div className={styles.footer}>
      <Button appearance="secondary" onClick={() => setStep('methods')} disabled={submitting}>Back</Button>
      <Button appearance="primary" onClick={handleCashPay} disabled={chargeTotalCents <= 0 || !cashDate || submitting}>
        {submitting ? 'Processing…' : `Pay ${formatMoney(chargeTotalCents)}`}
      </Button>
    </div>
  );

  const renderAchStep = () => {
    // Live-computed for display; submit-time re-computes the same string for
    // hashing. They agree unless the clock crosses a day boundary between
    // render and submit — the same rare sub-second race the website accepts.
    const liveDisclosureText = buildAchDisclosureText(chargeTotalCents / 100);
    const achInputClass = (hasError: boolean) =>
      hasError ? `${styles.achInput} ${styles.achInputError}` : styles.achInput;
    return (
      <>
        <div className={styles.breadcrumb}>
          <span className={styles.title}>Payment for order #{orderNumber ?? ''}</span>
          <ChevronRight16Regular className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCrumb}>Bank account (ACH)</span>
        </div>

        <div className={styles.cardPanel}>
          <div className={styles.cardPanelLabel}>Bank account details</div>
          <div className={styles.achInputRow}>
            <label className={styles.achLabel} htmlFor="achHolder">Name on account</label>
            <input
              id="achHolder"
              className={achInputClass(!!achErrors.accountHolderName)}
              value={achAccountHolderName}
              onChange={(e) => handleAchHolderNameChange(e.target.value)}
              autoComplete="off"
            />
            {achErrors.accountHolderName && <span className={styles.achError}>{achErrors.accountHolderName}</span>}
          </div>

          <div className={styles.achInputRow}>
            <span className={styles.achLabel}>Account type</span>
            <div className={styles.achTwoCol}>
              {(['checking', 'savings'] as AchAccountType[]).map((type) => {
                const selected = achAccountType === type;
                return (
                  <div
                    key={type}
                    className={`${styles.modeCard} ${selected ? styles.modeCardSelected : ''}`}
                    onClick={() => setAchAccountType(type)}
                    role="radio"
                    aria-checked={selected}
                  >
                    <div className={`${styles.modeRadio} ${selected ? styles.modeRadioSelected : ''}`}>
                      {selected && <div className={styles.modeRadioDot} />}
                    </div>
                    <span className={styles.modeLabel} style={{ textTransform: 'capitalize' }}>{type}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.achInputRow}>
            <label className={styles.achLabel} htmlFor="achRouting">Routing number</label>
            <input
              id="achRouting"
              className={achInputClass(!!achErrors.routingNumber)}
              value={achRoutingNumber}
              onChange={(e) => handleAchRoutingChange(e.target.value)}
              inputMode="numeric"
              placeholder="9 digits"
              autoComplete="off"
            />
            {achErrors.routingNumber && <span className={styles.achError}>{achErrors.routingNumber}</span>}
          </div>

          <div className={styles.achTwoCol}>
            <div className={styles.achInputRow}>
              <label className={styles.achLabel} htmlFor="achAccount">Account number</label>
              <input
                id="achAccount"
                className={achInputClass(!!achErrors.accountNumber)}
                value={achAccountNumber}
                onChange={(e) => handleAchAccountNumberChange(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
              />
              {achErrors.accountNumber && <span className={styles.achError}>{achErrors.accountNumber}</span>}
            </div>
            <div className={styles.achInputRow}>
              <label className={styles.achLabel} htmlFor="achAccountConfirm">Confirm account</label>
              <input
                id="achAccountConfirm"
                className={achInputClass(!!achErrors.confirmAccountNumber)}
                value={achConfirmAccountNumber}
                onChange={(e) => handleAchConfirmAccountNumberChange(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
              />
              {achErrors.confirmAccountNumber && <span className={styles.achError}>{achErrors.confirmAccountNumber}</span>}
            </div>
          </div>
        </div>

        {/* NACHA authorization — separate panel by design. Rule 1.5 requires
            ACH debit authorization to be "clear and conspicuous" and a
            separate affirmative action from any other consent (terms of
            service, rental agreement). Combining them would fail the standard
            even if the text covered both obligations. */}
        <div className={styles.achAuthPanel}>
          <div className={styles.cardPanelLabel}>Authorization</div>
          <p className={styles.achDisclosure}>{liveDisclosureText}</p>
          <Checkbox
            checked={achAuthorized}
            onChange={(_, data) => handleAchAuthorizedChange(data.checked === true)}
            label="The account holder authorizes this one-time electronic debit."
          />
          {achErrors.achAuthorized && <span className={styles.achError}>{achErrors.achAuthorized}</span>}
          <span className={styles.achSettlementNote}>
            ACH settles in 1–3 business days. A return (NSF, closed account) can reverse this
            payment days later — the order will reflect that automatically via the eCheck.Net
            returns webhook.
          </span>
        </div>

        {/* No technology fee on ACH, so there's no fee to waive here (the
            card path keeps its waive affordance). */}

        {submitError && <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>{submitError}</div>}
      </>
    );
  };

  const renderAchFooter = () => (
    <div className={styles.footer}>
      <Button appearance="secondary" onClick={() => setStep('methods')} disabled={submitting}>Back</Button>
      <Button
        appearance="primary"
        onClick={handleAchPay}
        disabled={chargeTotalCents <= 0 || !achAuthorized || !acceptJsReady || submitting}
      >
        {submitting
          ? 'Authorizing…'
          : !acceptJsReady
            ? 'Loading payment SDK…'
            : `Authorize ACH ${formatMoney(chargeTotalCents)}`}
      </Button>
    </div>
  );

  if (open && successAmountCents !== null) {
    const customerLabel = (customerName || '').trim() || 'this customer';
    return (
      <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
        <DialogSurface className={styles.surface}>
          <DialogBody className={styles.body}>
            <div className={styles.successFullBody}>
              <div className={styles.successPanel}>
                <div className={styles.successCheckRing}>
                  <Checkmark24Filled />
                </div>
                <div className={styles.successAmount}>
                  {successMode === 'authorize' ? 'Authorized' : 'Paid'} {formatMoney(successAmountCents)}
                </div>
                {successDeduped && (
                  <div className={styles.dedupeBanner} role="status">
                    <Info20Regular className={styles.dedupeBannerIcon} />
                    <span>
                      This card is already saved for {customerLabel}. We reused
                      the existing card on file instead of saving a duplicate.
                    </span>
                  </div>
                )}
                <Button appearance="primary" onClick={onClose}>Close</Button>
              </div>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className={styles.surface}>
        <DialogBody className={styles.body}>
          <aside className={styles.rail}>
            <div>
              <div className={styles.railLabel}>Amount due</div>
              <div className={styles.railAmount}>{formatMoney(totalDueCents)}</div>
            </div>
            <div className={styles.railDivider} />
            <div>
              <div className={styles.railRow}>
                <span style={{ fontWeight: tokens.fontWeightSemibold }}>Order amount</span>
                <span style={{ fontWeight: tokens.fontWeightSemibold }}>{formatMoney(orderAmountInCents)}</span>
              </div>
              <div className={styles.railSubRow}>
                <span>Paid</span>
                <span>{formatMoney(orderPaidInCents)}</span>
              </div>
            </div>
          </aside>

          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          />

          <section className={styles.main}>
            <div className={styles.mainScroll}>
              {step === 'methods' && renderMethodsStep()}
              {step === 'credit_card' && renderCreditCardStep()}
              {step === 'ach' && renderAchStep()}
              {step === 'cash_other' && renderCashOtherStep()}
            </div>
            {step === 'credit_card' && renderCreditCardFooter()}
            {step === 'ach' && renderAchFooter()}
            {step === 'cash_other' && renderCashOtherFooter()}
          </section>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default PaymentDialog;
